import { isRtl } from './i18n'
import { displayDate } from './dates'

/** A date cell prints as it does on screen (Taiga #1538); anything else is left as it came. */
const pdfCell = (value) => (typeof value === 'string' ? displayDate(value) : (value ?? ''))

/*
 * A table as a PDF FILE, downloaded on the click.
 *
 * WHY RASTERISED. jsPDF writes text glyph by glyph in logical order with no bidi and no
 * Arabic shaping, so an Arabic campaign name comes out as disconnected letters in reverse —
 * which is exactly why the legacy's own exported reports are unreadable in Arabic. The sheet
 * is therefore laid out as HTML off-screen, photographed with html2canvas, and the photograph
 * is what the PDF carries. The browser does the shaping; the file keeps what it drew.
 *
 * The price is that the text is not selectable. The alternative is a file nobody can read.
 *
 * `features/dashboard/exportReport.jsx` reached the same conclusion for the statistics
 * report; this is the same trade made reusable for any list.
 */

/** A4 at 96dpi — the size html2canvas photographs and jsPDF lays down. */
const PAGE_WIDTH = 794
const PAGE_HEIGHT = 1123
const MARGIN = 44

/** 2x the 96dpi page: ~192dpi, sharp on screen and clean on an office printer. */
const CAPTURE_SCALE = 2

/** Fixed, so pagination is arithmetic rather than measurement. */
const ROW_HEIGHT = 38
const HEAD_HEIGHT = 34

/**
 * A cell's `value()` returns HTML — that is how a name carries its template on a second
 * line — so anything coming from the database has to be escaped on the way in. Exported
 * because the callers are the ones composing those cells.
 */
export const escapeHtml = (text) =>
  String(text ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c])

const esc = escapeHtml

/**
 * @param {object}   options
 * @param {string}   options.title      what the list is
 * @param {string}   [options.subtitle] the filter it was taken under, if any
 * @param {Array}    options.columns    `{ header, value(row), align?, width? }`
 * @param {Array}    options.rows
 * @param {string}   options.fileName   without the extension
 * @param {object}   [options.labels]   `{ generatedOn, page, of, empty }`
 * @param {Array}    [options.charts]   `{ title, image, height? }` — PNG data URLs from
 *   `lib/chartImage`. They lead the document: the shapes answer "how did this go" before
 *   the table answers "which one".
 */
export async function downloadTablePdf({
  title,
  subtitle = '',
  columns,
  rows,
  fileName,
  labels = {},
  charts = [],
}) {
  const dir = isRtl ? 'rtl' : 'ltr'
  /* WRITTEN OUT, NOT LOCALISED. `toLocaleString` in Arabic returns "20/09/2026، 04:48 م" —
     Latin digits and slashes with an Arabic PM marker on the end — and that mixture inside
     an RTL paragraph gets reordered by the bidi algorithm into "/2026/09، 04:48 م20". The
     stamp is a timestamp, not prose: Gregorian, 24-hour, no marker, nothing to reorder. */
  const now = new Date()
  const two = (n) => String(n).padStart(2, '0')
  const stamp =
    `${now.getFullYear()}-${two(now.getMonth() + 1)}-${two(now.getDate())} ` +
    `${two(now.getHours())}:${two(now.getMinutes())}`

  /* How many rows fit under the heading, once the page furniture has taken its share.
     Counted against the row's DRAWN height — the hairline under each one is a pixel the
     row occupies, and twenty-odd of them is most of a row's worth of drift by the foot
     of the page. */
  const bodyHeight = PAGE_HEIGHT - MARGIN * 2 - 92 /* header */ - 28 /* footer */ - HEAD_HEIGHT
  const perPage = Math.max(1, Math.floor(bodyHeight / (ROW_HEIGHT + 1)))
  const sheets = rows.length ? chunk(rows, perPage) : [[]]

  /* The charts take the first sheet and the table starts on the next, rather than the two
     sharing one. A chart squeezed under a heading to leave room for four rows of a table
     serves neither. */
  const pages = [
    ...(charts.length ? [chartsHtml({ charts, title, subtitle, stamp, dir, labels })] : []),
    /* The note rides on the first sheet only. Repeated at the head of every page it
       stops being information and becomes furniture. */
    ...sheets.map((sheet, index) =>
      sheetHtml({ sheet, title, subtitle, columns, dir, stamp, labels, note: index === 0 ? labels.note : '' }),
    ),
  ]

  /* OFF-SCREEN, NOT HIDDEN. html2canvas needs a laid-out box with real dimensions;
     `display:none` photographs as nothing. */
  const host = document.createElement('div')
  host.setAttribute('aria-hidden', 'true')
  host.style.cssText = `position:fixed;inset-inline-start:-10000px;top:0;width:${PAGE_WIDTH}px;`
  /* Numbered once the full set is known, so the table's own sheets do not claim to be
     page one when a chart sheet is sitting in front of them. */
  host.innerHTML = pages.map((page, index) => page.replace('{{FOOT}}', `${index + 1} / ${pages.length}`)).join('')
  document.body.appendChild(host)

  try {
    /* Loaded lazily so a bundle that never exports never pays for them — together they
       are larger than most feature chunks. */
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import('html2canvas'),
      import('jspdf'),
    ])

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true })
    const width = pdf.internal.pageSize.getWidth()
    const height = pdf.internal.pageSize.getHeight()

    const pages = [...host.children]
    for (const [index, page] of pages.entries()) {
      const canvas = await html2canvas(page, {
        scale: CAPTURE_SCALE,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: PAGE_WIDTH,
        windowHeight: PAGE_HEIGHT,
      })
      if (index > 0) pdf.addPage()
      /* JPEG rather than PNG: a photograph of a page is a photograph, and PNG held the
         same sheet at several times the bytes for no visible difference. */
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.94), 'JPEG', 0, 0, width, height, undefined, 'FAST')
    }

    pdf.save(`${fileName}.pdf`)
    return { ok: true, pages: pages.length }
  } finally {
    host.remove()
  }
}

function chunk(list, size) {
  const out = []
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size))
  return out
}

/*
 * ONE SHEET. Printed ink, not screen chrome: white ground, dark text, hairline rules, and a
 * single accent line under the heading. No zebra banding and no filled header — both cost
 * toner on every page and neither helps anyone read seven columns of figures.
 *
 * `{{FOOT}}` is filled in once every page exists; a sheet cannot know its own number.
 */
function frame({ dir, title, subtitle, stamp, labels, body }) {
  return `
<section dir="${dir}" style="
  position:relative;width:${PAGE_WIDTH}px;height:${PAGE_HEIGHT}px;box-sizing:border-box;
  padding:${MARGIN}px;background:#fff;color:#18181b;
  font-family:'Segoe UI','Noto Sans Arabic',system-ui,sans-serif;">

  <header style="margin-bottom:18px">
    <!-- NO letter-spacing. Arabic is a joined script: spacing the glyphs apart is what
         severed the heading into loose letters in the rasterised sheet while the body
         text, which never had it, came out correctly shaped. Tightening is a Latin habit. -->
    <h1 style="margin:0;font-size:19px;font-weight:600;color:#18181b">${esc(title)}</h1>
    ${subtitle ? `<p style="margin:6px 0 0;font-size:12px;color:#52525b">${esc(subtitle)}</p>` : ''}
    <p style="margin:6px 0 0;font-size:11px;color:#71717a">${esc(labels.generatedOn ?? '')} <span dir="ltr">${esc(stamp)}</span></p>
  </header>
  <div style="height:2px;background:#4f46e5;margin-bottom:14px"></div>

  ${body}

  <footer style="
    position:absolute;inset-inline:${MARGIN}px;bottom:${MARGIN - 14}px;
    display:flex;justify-content:space-between;font-size:10.5px;color:#a1a1aa">
    <span>${esc(title)}</span>
    <span dir="ltr">{{FOOT}}</span>
  </footer>

  <style>
    td { height:${ROW_HEIGHT}px;border-bottom:1px solid #ededf0;
         padding:4px 6px;vertical-align:middle }
    th { padding-inline:6px }
    /* A ROW IS EXACTLY ITS HEIGHT. A height on a td is a floor, not a ceiling: a cell
       that wrapped simply grew, and the page then held fewer rows than the arithmetic
       above had allotted it, so the last one ran off the bottom. The clamp lives on an
       inner box, where overflow actually applies. (No backticks in here — this comment
       is inside a template literal.) */
    .tpdf-cell { height:${ROW_HEIGHT - 8}px;overflow:hidden;display:flex;
                 flex-direction:column;justify-content:center;line-height:1.3 }
    .tpdf-num { font-variant-numeric:tabular-nums }
  </style>
</section>`
}

/*
 * THE CHARTS SHEET, which leads the document.
 *
 * Each chart is a PNG already drawn by ECharts at 3x (see `lib/chartImage`), so what this
 * lays out is images with a caption apiece — the page capture never has to redraw a chart
 * and cannot soften one.
 */
function chartsHtml({ charts, title, subtitle, stamp, dir, labels }) {
  const body = charts
    .map(
      (c) => `
    <figure style="margin:0 0 18px">
      <figcaption style="margin:0 0 8px;font-size:12.5px;font-weight:600;color:#3f3f46">${esc(c.title)}</figcaption>
      <img src="${c.image}" alt="" style="display:block;width:100%;height:${c.height ?? 250}px;object-fit:contain" />
    </figure>`,
    )
    .join('')

  return frame({ dir, title, subtitle, stamp, labels, body })
}

function sheetHtml({ sheet, title, subtitle, columns, dir, stamp, labels, note }) {
  const head = columns
    .map(
      (c) =>
        `<th style="text-align:${c.align === 'center' ? 'center' : 'start'};${
          c.width ? `width:${c.width};` : ''
        }">${esc(c.header)}</th>`,
    )
    .join('')

  const body = sheet.length
    ? sheet
        .map(
          (row) =>
            `<tr>${columns
              .map(
                (c) =>
                  `<td style="text-align:${
                    c.align === 'center' ? 'center' : 'start'
                  }"><div class="tpdf-cell">${pdfCell(c.value(row))}</div></td>`,
              )
              .join('')}</tr>`,
        )
        .join('')
    : `<tr><td colspan="${columns.length}" style="text-align:center;color:#71717a;padding:28px 0">${esc(
        labels.empty ?? '',
      )}</td></tr>`

  const table = `
  ${
    note
      ? `<p style="margin:0 0 14px;padding:10px 12px;border-inline-start:3px solid #4f46e5;
                   background:#f6f6fb;font-size:11.5px;color:#3f3f46">${esc(note)}</p>`
      : ''
  }
  <table style="width:100%;border-collapse:collapse;font-size:11.5px;table-layout:fixed">
    <thead>
      <tr style="height:${HEAD_HEIGHT}px;border-bottom:1.5px solid #d4d4d8;color:#52525b;font-weight:600">
        ${head}
      </tr>
    </thead>
    <tbody>${body}</tbody>
  </table>`

  return frame({ dir, title, subtitle, stamp, labels, body: table })
}
